import { isLightColour } from '@app/utils/colourFunctions';
import { hashCode, intToRGB } from '@app/utils/generateColors';
import { Avatar, Tooltip } from 'antd';
import { AvatarSize } from 'antd/es/avatar/AvatarContext';
import { useEffect, useState } from 'react';

type Props = {
  fullName: string;
  value: string;
  size?: AvatarSize;
  isSelected?: boolean;
  imageUrl?: string;
  personId?: number;
  showTooltip?: boolean;
  responsive?: boolean;
};

const AVATAR_DIMENSIONS = {
  small: 24,
  default: 32,
  large: 40,
} as const;

const resolveAvatarDimension = (size: AvatarSize): number | undefined => {
  if (typeof size === 'number') {
    return size;
  }

  if (typeof size === 'string') {
    return AVATAR_DIMENSIONS[size as keyof typeof AVATAR_DIMENSIONS];
  }

  return undefined;
};

const AppAvatar = ({
  fullName,
  value,
  size = 'default',
  isSelected = false,
  showTooltip = false,
  responsive = false,
}: Props) => {
  const [imageUrl, setImageUrl] = useState('');
  useEffect(() => {
    //   const loadImages = async () => {
    //     const blob = await getEmpPhoto(personId as number);
    //     if (blob) setImageUrl(URL.createObjectURL(blob));
    //   };
    //   loadImages();

    // console.log(personId);
    setImageUrl('');
  }, []);

  const avatarDimension = resolveAvatarDimension(size);

  // Responsive styling to fit avatar within parent container
  const responsiveClasses = responsive ? 'max-w-full max-h-full !w-auto !h-auto' : '';
  const baseDimensions = avatarDimension
    ? {
        width: avatarDimension,
        height: avatarDimension,
        minWidth: avatarDimension,
        minHeight: avatarDimension,
        lineHeight: `${avatarDimension - 4}px`,
      }
    : undefined;
  const responsiveDimensions =
    responsive && avatarDimension
      ? {
          width: '100%',
          height: '100%',
          maxWidth: `${avatarDimension}px`,
          maxHeight: `${avatarDimension}px`,
          minWidth: `${Math.min(avatarDimension, 20)}px`,
          minHeight: `${Math.min(avatarDimension, 20)}px`,
          fontSize: `${Math.max(10, Math.round(avatarDimension * 0.35))}px`,
        }
      : undefined;

  return (
    <Tooltip title={showTooltip ? fullName : undefined}>
      {isSelected ? (
        imageUrl ? (
          <img src={imageUrl} className={`rounded-full object-cover ${responsiveClasses}`} style={baseDimensions} />
        ) : (
          <Avatar
            className={`border-solid border-primary border-2 text-sm font-normal ${responsiveClasses}`}
            size={size}
            style={{
              ...baseDimensions,
              backgroundColor: fullName ? '#' + intToRGB(hashCode(fullName)) : '',
              color: fullName && isLightColour('#' + intToRGB(hashCode(fullName))) ? 'black' : 'white',
              ...responsiveDimensions,
            }}
            src={imageUrl || undefined}
            shape="circle"
          >
            {!imageUrl && <>{value}</>}
          </Avatar>
        )
      ) : (
        'me'
      )}
    </Tooltip>
  );
};

export default AppAvatar;
